import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Skeleton,
  SkeletonItem,
} from '@fluentui/react-components';
import {
  ArrowLeft,
  Check,
  CircleHelp,
  Minus,
  Play,
  RefreshCw,
  TriangleAlert,
  X,
} from 'lucide-react';

import { ApiError } from '../api/client';
import ActivateWaveDialog from '../components/ActivateWaveDialog';
import CampaignStepper from '../components/CampaignStepper';
import { useMintReadiness } from '../components/MintReadinessPanel';
import SignInHelperCheck from '../components/SignInHelperCheck';
import RouteFlight from '../components/RouteFlight';
import TenantLabel from '../components/TenantLabel';
import { ContextCard, DetailLayout } from '../components/ContextColumn';
import { Fact, FactList, PageHeader, SectionPanel, StatePill } from '../components/abPrimitives';
import {
  MAPPINGS_FROZEN_DETAIL,
  MAPPINGS_FROZEN_RULE,
} from '../components/identity/MappingsFrozenBanner';
import { useCampaignPreflight } from '../hooks/useCampaignPreflight';
import { useMigrationDetailQuery } from '../hooks/useMigrationDetail';
import { useSignInHelper } from '../hooks/useSignInHelper';
import { deviceCodeBlockState } from '../lib/deviceCodeBlocking';
import { deriveCampaignLifecycle } from '../lib/campaignLifecycle';
import { deriveCampaignStatus } from '../lib/campaignStatus';
import type { PreflightCheck, PreflightState } from '../lib/campaignPreflight';
import { migrationTypeLabel } from '../lib/vocabulary';
import { ACCENT, IDENTIFIER, NUMERIC, STATE, STROKE, SURFACE, TEXT } from '../theme/tokens';

// FD-493 (R-FD-051 D3) — the wave-activation preflight, on a route of its own.
//
// ── WHY A ROUTE AND NOT A PANEL ────────────────────────────────────────────
//
// Activation mints a tenant-join credential in a customer's destination tenant
// that Rubix cannot revoke (FD-ADR-034 C3), reserves one migration licence per
// assigned device (FD-ADR-041 §1), and is the step every device is waiting on.
// The checks that precede it are a page an operator READS, not a hint beside a
// button — which is what a dedicated route makes them, with an address they can
// send to someone else.
//
// It is a SIBLING of the campaign layout rather than a fifth tab, deliberately:
// the campaign's four tabs are the record's contents, and this is a one-way
// approach to a decision. Mounting it under the layout would have put the tab
// row and the context column's own Activate button on the same screen as this
// one — two ways to press the same irreversible thing.
//
// ── THE BANNER IS THE REASON THIS TASK EXISTS ──────────────────────────────
//
// Mockup 4 draws an amber panel asserting that activating a wave freezes which
// devices belong to the campaign — that none may be added, removed or moved to
// another campaign afterwards. That claim is FALSE, and R-FD-051 D1 refuses it
// against three ratified decisions:
//
// (Its exact wording is deliberately NOT reproduced anywhere in this file, on
// the FD-ADR-034 precedent: a quoted sentence outlives its own disclaimer, and
// a grep for the claim across the page an operator reads must come back empty.
// R-FD-051 D1 in `docs/TASKS.md` is where the original text is recorded.)
//
//   • `manager/add-devices` has NO `BprtStatus` guard, by design, and its
//     active-wave branch exists to TOP UP the licence reservation
//     (FD-ADR-041 §5);
//   • FD-ADR-035 §3 makes remove + add a failed device's only retry path, so
//     locking membership would remove the platform's only recovery route;
//   • FD-ADR-036 narrowed the identity freeze to ADDITIVE-ONLY precisely so a
//     late mapping can release a device held at `409 IDENTITY_NOT_READY`
//     *while the wave runs*.
//
// So the panel says what activation actually does. The mapping clause is
// imported from `MappingsFrozenBanner` rather than rewritten: that sentence is
// already reviewed and tested, and a second wording of a freeze rule on the one
// page whose job is to be truthful about activation is exactly how the two
// drift apart.
//
// ── NOTHING ON THIS PAGE GATES THE BUTTON ──────────────────────────────────
//
// `lib/campaignPreflight.ts` opens with this and it is repeated here because
// this is the file where someone would implement the opposite in one line.
// `manager/get-mint-readiness` says it about its own probe: *"It never gates
// activation … blocking on it would trade a named error for a false block."*
// Consent is granted by an admin in a tenant Rubix does not own; a licence
// balance is ATC's answer; a manifest arrives when a device boots. `activate-wave`
// refuses on its OWN terms with its OWN named codes, and those refusals are the
// control. The Activate button's `disabled` therefore reads NOTHING from the
// preflight view — asserted, and mutation-tested, in this page's suite.

/** Fluent tone per check state — see the note in `CheckRow` on why two share `subtle`. */
const BADGE_TONE: Record<PreflightState, 'success' | 'warning' | 'danger' | 'subtle'> = {
  pass: 'success',
  warn: 'warning',
  fail: 'danger',
  unknown: 'subtle',
  'not-applicable': 'subtle',
};

export default function MigrationActivatePage() {
  const { migrationId } = useParams<{ migrationId: string }>();
  const navigate = useNavigate();

  // Declared above every early return — FD-188, the trap this console has
  // already paid for once.
  const [dialogOpen, setDialogOpen] = useState(false);

  // No `?live=1` interval here. The campaign layout polls because a device grid
  // moves; this page is read once before a decision, and a poll would put the
  // whole detail read on a loop behind a screen nobody is watching change.
  const query = useMigrationDetailQuery(migrationId);
  const { preflight, probesPending } = useCampaignPreflight(migrationId, query.data);
  // FD-592 — the Sign-in helper check. Both hooks run unconditionally, above every
  // early return (FD-188). The readiness read is the SAME query the preflight hook
  // already holds (same key), so this adds no request.
  const helper = useSignInHelper(Boolean(migrationId));
  const mintReadiness = useMintReadiness(migrationId ?? '', Boolean(migrationId));

  const detailPath = migrationId ? `/migrations/${encodeURIComponent(migrationId)}/overview` : '/migrations';

  if (query.isError && query.error instanceof ApiError && query.error.status === 404) {
    return (
      <div className="max-w-xl">
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>Campaign not found</MessageBarTitle>
            No campaign exists with ID{' '}
            <span className="text-xs" style={IDENTIFIER}>
              {migrationId}
            </span>
            .
          </MessageBarBody>
          <MessageBarActions>
            <Link to="/migrations" style={{ color: ACCENT.pressed }}>
              Back to campaigns
            </Link>
          </MessageBarActions>
        </MessageBar>
      </div>
    );
  }

  if (query.isLoading) return <ActivateSkeleton />;

  if (query.isError || !query.data || !preflight) {
    return (
      <div className="max-w-xl">
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Could not load campaign</MessageBarTitle>
            {query.error instanceof Error ? query.error.message : 'Unexpected error'}
          </MessageBarBody>
          <MessageBarActions>
            <Button
              appearance="subtle"
              size="small"
              icon={<RefreshCw size={14} strokeWidth={1.5} />}
              onClick={() => query.refetch()}
            >
              Retry
            </Button>
          </MessageBarActions>
        </MessageBar>
      </div>
    );
  }

  const { migration } = query.data;
  const devices = query.data.devices ?? [];
  const isCloud = migration.migrationType === 'T2T';
  const campaignStatus = deriveCampaignStatus(migration, devices);
  const lifecycle = deriveCampaignLifecycle(migration, devices);

  // The same predicate `WavePanel` offers its Activate button on, and the same
  // one `Test-MappingsFrozen` uses: `BprtStatus == active`. A wave that is
  // already running has nothing to activate, so the page says so rather than
  // offering a button whose only outcome is a server refusal (FD-ADR-043 D4).
  const alreadyActive = (migration.bprtStatus ?? '').toLowerCase() === 'active';

  // FD-442 (FD-ADR-042 §7) — the lifetime the SERVER will apply, never a
  // constant here. Absent against a Manager older than FD-442, in which case the
  // sentence is written without a number rather than with a guessed one.
  const defaultExpiryDays = query.data.waveDefaults?.bprtExpiryDays;
  const deviceCount = migration.deviceCount ?? 0;

  // Steps 4–6 of FD-490's spine, reused rather than restated. Two renderings of
  // one journey is how they disagree.
  const laterSteps = lifecycle.steps.slice(3);

  return (
    <div className="flex flex-col gap-6">
      <Link
        to={detailPath}
        className="inline-flex items-center gap-1.5 text-[13px] no-underline hover:underline"
        style={{ color: ACCENT.pressed }}
      >
        <ArrowLeft size={14} strokeWidth={1.5} />
        Back to campaign
      </Link>

      <PageHeader
        title={migration.migrationName}
        badge={<StatePill state={campaignStatus.state}>{campaignStatus.label}</StatePill>}
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-2">
            {isCloud ? (
              <TenantLabel
                domain={migration.sourceTenantDomain}
                tenantId={migration.sourceTenantId}
              />
            ) : (
              <span>On-premises domain</span>
            )}
            <RouteFlight />
            <TenantLabel domain={migration.destTenantDomain} tenantId={migration.destTenantId} />
          </span>
        }
      />

      <CampaignStepper view={lifecycle} />

      <DetailLayout
        context={
          <>
            <ActivationTruthPanel
              defaultExpiryDays={defaultExpiryDays}
              deviceCount={deviceCount}
            />

            <ContextCard title="Wave summary">
              <FactList>
                <Fact label="Name">{migration.migrationName}</Fact>
                <Fact label="Type">{migrationTypeLabel(migration.migrationType)}</Fact>
                <Fact label="Wave">{migration.migrationWave}</Fact>
                <Fact label="Group">
                  {migration.migrationGroupName ?? migration.migrationGroupId ?? 'No group'}
                </Fact>
                {isCloud && (
                  <Fact label="Source tenant">
                    <TenantLabel
                      domain={migration.sourceTenantDomain}
                      tenantId={migration.sourceTenantId}
                    />
                  </Fact>
                )}
                <Fact label="Destination">
                  <TenantLabel
                    domain={migration.destTenantDomain}
                    tenantId={migration.destTenantId}
                    // FD-ADR-029 §1 — a Hybrid campaign with no explicit
                    // destination lands in the DEPLOYMENT's own tenant. A real
                    // answer, named as one; an em-dash would read as unset.
                    fallback={isCloud ? '—' : 'Deployment tenant'}
                  />
                </Fact>
                <Fact label="Devices assigned" numeric>
                  {deviceCount.toLocaleString()}
                </Fact>
              </FactList>
            </ContextCard>

            <ContextCard title="Next steps">
              <ol className="m-0 flex list-none flex-col gap-3 p-0">
                {laterSteps.map((step, index) => (
                  <li key={step.id} className="flex items-start gap-2.5">
                    <span
                      aria-hidden="true"
                      className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
                      style={{ backgroundColor: ACCENT.tint, color: ACCENT.pressed }}
                    >
                      {index + 4}
                    </span>
                    <span className="min-w-0">
                      <span
                        className="block text-[12px] font-medium"
                        style={{ color: TEXT.primary }}
                      >
                        {step.label}
                      </span>
                      <span
                        className="block text-[11px] leading-snug"
                        style={{ color: TEXT.secondary }}
                      >
                        {step.caption}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
            </ContextCard>
          </>
        }
      >
        <SectionPanel
          title={
            <span className="flex flex-wrap items-center gap-3">
              Activate wave
              <Badge appearance="tint" color="informative">
                Step 3 of 6
              </Badge>
            </span>
          }
          description="Review the checks below, then mint the wave credential. None of them blocks activation — they are what the console can see before you commit."
          action={
            <span className="text-[12px]" style={{ color: TEXT.secondary }}>
              {probesPending ? (
                'Checking…'
              ) : (
                <>
                  <span style={NUMERIC}>{preflight.passed}</span> of{' '}
                  <span style={NUMERIC}>{preflight.applicable}</span> checks passed
                  {preflight.unknownCount > 0 && (
                    <>
                      {' · '}
                      <span style={NUMERIC}>{preflight.unknownCount}</span> not verified
                    </>
                  )}
                </>
              )}
            </span>
          }
          bodyClassName="px-0 pb-0"
        >
          <ul className="m-0 flex list-none flex-col p-0" aria-label="Preflight checks">
            {preflight.checks.map((check) => (
              <CheckRow key={check.id} check={check} />
            ))}
          </ul>
        </SectionPanel>

        {/* FD-592 (FD-ADR-047 §5) — advisory, and deliberately NOT one of the
            preflight checks above: it is about this BROWSER, not the campaign, and
            it must never be counted into "N of M checks passed". It gates nothing. */}
        <SectionPanel
          title="Sign-in"
          description="Activation signs in as the minting account in a new browser tab first; device code and a pasted token remain available in the dialog."
        >
          <SignInHelperCheck
            helper={helper}
            blockState={deviceCodeBlockState(mintReadiness.data)}
          />
        </SectionPanel>

        <SectionPanel title="What happens next">
          <ol className="m-0 flex list-none flex-col gap-3 p-0">
            <HappensNext index={1}>
              AirBridge brokers an interactive sign-in as the destination tenant&rsquo;s minting
              service account. The resulting token is redeemed on the server and never reaches
              this browser.
            </HappensNext>
            <HappensNext index={2}>
              The wave&rsquo;s Bulk PRT is minted and stored in this deployment&rsquo;s Key
              Vault, and{' '}
              {deviceCount > 0 ? (
                <>
                  <span style={NUMERIC}>{deviceCount}</span>{' '}
                  {deviceCount === 1 ? 'migration licence is' : 'migration licences are'} reserved
                  &mdash; one per assigned device.
                </>
              ) : (
                <>one migration licence per assigned device is reserved.</>
              )}
            </HappensNext>
            <HappensNext index={3}>
              You then build the wave&rsquo;s provisioning package. Until that exists every device
              is held at <span style={IDENTIFIER}>PACKAGE_NOT_READY</span> rather than joining
              without one.
            </HappensNext>
          </ol>
        </SectionPanel>

        {alreadyActive ? (
          <MessageBar intent="info">
            <MessageBarBody>
              <MessageBarTitle>This wave is already active</MessageBarTitle>
              Its credential has been minted, so there is nothing to activate. Complete or abort
              the wave from the campaign&rsquo;s wave panel.
            </MessageBarBody>
          </MessageBar>
        ) : null}

        {/* The commit row. Not fixed to the viewport: a floating bar would sit
            over the shell's own content column and would be the only element in
            the console that does. */}
        <div
          className="mt-2 flex flex-wrap items-center justify-end gap-3 rounded-lg border px-5 py-4"
          style={{ borderColor: STROKE.base, backgroundColor: SURFACE.base }}
        >
          <Button appearance="secondary" onClick={() => navigate(detailPath)}>
            Cancel
          </Button>
          {alreadyActive ? (
            <Button appearance="primary" onClick={() => navigate(detailPath)}>
              Back to campaign
            </Button>
          ) : (
            <Button
              appearance="primary"
              size="large"
              icon={<Play size={16} strokeWidth={1.5} />}
              onClick={() => setDialogOpen(true)}
            >
              Activate wave
            </Button>
          )}
        </div>
      </DetailLayout>

      {/* Mounted CLOSED and toggled — a Fluent Dialog mounted already-open never
          enters the accessibility tree (FLUENT_UI_ADDENDUM). The dialog itself
          is REUSED, never re-implemented: it mints a real credential Rubix
          cannot revoke, and a second implementation would drift from the first
          exactly where correctness lives. */}
      <ActivateWaveDialog
        open={dialogOpen}
        migrationId={migration.migrationId}
        deviceCount={migration.deviceCount}
        defaultExpiryDays={defaultExpiryDays}
        migrationName={migration.migrationName}
        migrationType={migration.migrationType}
        destinationDomain={migration.destTenantDomain}
        onClose={() => setDialogOpen(false)}
        onDone={() => {
          void query.refetch();
        }}
      />
    </div>
  );
}

// ——— the preflight rows ————————————————————————————————————————————————

function CheckRow({ check }: { check: PreflightCheck }) {
  const { icon, tone } = MARK[check.state];

  return (
    <li
      className="flex items-start gap-3 border-t px-5 py-3 first:border-t-0"
      style={{ borderColor: STROKE.subtle }}
    >
      <span
        aria-hidden="true"
        className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: tone.soft, color: tone.text }}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-medium" style={{ color: TEXT.primary }}>
          {check.label}
        </span>
        <span className="block text-[12px] leading-snug" style={{ color: TEXT.secondary }}>
          {check.detail}
        </span>
      </span>
      {/* Fluent's tinted Badge, not a hand-rolled chip: it supplies its own
          foreground, and every hand-rolled alternative would have to reproduce
          the contrast handling the primitive already has (FLUENT_UI_ADDENDUM).

          `unknown` and `not-applicable` SHARE `subtle` on purpose. Neither is a
          problem: one is "nobody answered", the other is "there is nothing to
          ask". Painting either amber alongside a real warning would erase the
          triage distinction an operator reads at a glance — a zero-config Hybrid
          campaign legitimately reports "Deployment default", and that must not
          read as a fault. The row text and the icon keep the two apart. */}
      <Badge appearance="tint" color={BADGE_TONE[check.state]}>
        {check.badge}
      </Badge>
    </li>
  );
}

const ICON = 12;

/** Icon + tone per state. Colour is never the only signal — the badge text names it. */
const MARK: Record<PreflightState, { icon: JSX.Element; tone: (typeof STATE)[keyof typeof STATE] }> =
  {
    pass: { icon: <Check size={ICON} strokeWidth={2} />, tone: STATE.success },
    warn: { icon: <TriangleAlert size={ICON} strokeWidth={2} />, tone: STATE.warning },
    fail: { icon: <X size={ICON} strokeWidth={2} />, tone: STATE.critical },
    unknown: { icon: <CircleHelp size={ICON} strokeWidth={2} />, tone: STATE.neutral },
    'not-applicable': { icon: <Minus size={ICON} strokeWidth={2} />, tone: STATE.neutral },
  };

function HappensNext({ index, children }: { index: number; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span
        aria-hidden="true"
        className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
        style={{ backgroundColor: ACCENT.tint, color: ACCENT.pressed }}
      >
        {index}
      </span>
      <span className="min-w-0 text-[13px] leading-relaxed" style={{ color: TEXT.secondary }}>
        {children}
      </span>
    </li>
  );
}

// ——— what activation actually does ——————————————————————————————————————

/**
 * The panel mockup 4 fills with "Activation locks campaign membership".
 *
 * See the module header for the three ratified decisions that claim contradicts.
 * The title leads with what activation DOES rather than with what it forbids,
 * because that is the sentence an operator needs before pressing the button, and
 * because a list of prohibitions invites the reader to supply the missing one.
 */
function ActivationTruthPanel({
  defaultExpiryDays,
  deviceCount,
}: {
  defaultExpiryDays?: number;
  deviceCount: number;
}) {
  return (
    <ContextCard title="Activation mints the wave credential">
      <ul className="m-0 flex list-none flex-col gap-3 p-0">
        <TruthPoint title={`Identity mappings — ${MAPPINGS_FROZEN_RULE}`}>
          {MAPPINGS_FROZEN_DETAIL}
        </TruthPoint>

        <TruthPoint title="Devices can still be added">
          Adding a device to a running wave tops up the licence reservation by one rather than
          being refused, and moving a failed device into a fresh campaign is how it is retried.
          {deviceCount > 0 && (
            <>
              {' '}
              Activating now reserves <span style={NUMERIC}>{deviceCount}</span>.
            </>
          )}
        </TruthPoint>

        <TruthPoint title="The credential expires, and completing the wave revokes it">
          {typeof defaultExpiryDays === 'number' ? (
            <>
              It lasts <span style={NUMERIC}>{defaultExpiryDays}</span>{' '}
              {defaultExpiryDays === 1 ? 'day' : 'days'} unless you change the lifetime when you
              activate.{' '}
            </>
          ) : (
            <>You choose its lifetime when you activate, up to 90 days. </>
          )}
          Completing the wave deletes the package user it was minted against, which kills every
          device token derived from it at once.
        </TruthPoint>
      </ul>
    </ContextCard>
  );
}

function TruthPoint({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <li>
      <span className="block text-[12px] font-semibold" style={{ color: TEXT.primary }}>
        {title}
      </span>
      <span className="mt-0.5 block text-[12px] leading-snug" style={{ color: TEXT.secondary }}>
        {children}
      </span>
    </li>
  );
}

function ActivateSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <Skeleton>
        <SkeletonItem size={16} style={{ width: '140px' }} />
      </Skeleton>
      <Skeleton>
        <SkeletonItem size={32} style={{ width: '40%' }} />
      </Skeleton>
      <Skeleton>
        <SkeletonItem size={128} style={{ width: '100%' }} />
      </Skeleton>
    </div>
  );
}
